import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Search,
  FileText,
  Users,
  CreditCard,
  Shield,
  Settings,
  Plug,
  Send,
  LayoutTemplate,
  ChevronRight,
  HelpCircle,
  MessageSquare,
  Zap,
  Key,
  Mail,
  PenTool,
  Rocket,
  Copy,
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  Star,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { knowledgeBaseApi } from "@/services/api";

// Icon mapping for categories
const iconMap = {
  Rocket,
  FileText,
  PenTool,
  Copy,
  Shield,
  Plug,
  CreditCard,
  HelpCircle,
  Users,
  Settings,
  BookOpen,
  LayoutTemplate,
  Send,
  Key,
  Zap,
  Mail,
  MessageSquare,
};

// Color classes for categories
const colorClasses = {
  "#6366f1": { text: "text-primary", bg: "bg-primary/10" },
  "#3b82f6": { text: "text-blue-500", bg: "bg-blue-500/10" },
  "#10b981": { text: "text-emerald-500", bg: "bg-emerald-500/10" },
  "#f59e0b": { text: "text-amber-500", bg: "bg-amber-500/10" },
  "#ef4444": { text: "text-red-500", bg: "bg-red-500/10" },
  "#8b5cf6": { text: "text-purple-500", bg: "bg-purple-500/10" },
  "#ec4899": { text: "text-pink-500", bg: "bg-pink-500/10" },
  "#06b6d4": { text: "text-cyan-500", bg: "bg-cyan-500/10" },
  "#f97316": { text: "text-orange-500", bg: "bg-orange-500/10" },
};

const getColorClasses = (color) =>
  colorClasses[color] || { text: "text-primary", bg: "bg-primary/10" };

export const KnowledgeBase = () => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState({});

  // Get current locale (e.g., 'en', 'fr', 'de')
  const locale = i18n.language?.split("-")[0] || "en";

  // Fetch categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["kb-categories", locale],
    queryFn: async () => {
      const response = await knowledgeBaseApi.getCategories(locale);
      return response.data?.data || response.data || [];
    },
  });

  // Fetch articles (optionally filtered by category)
  const { data: articlesData, isLoading: articlesLoading } = useQuery({
    queryKey: ["kb-articles", selectedCategory?.slug, locale],
    queryFn: async () => {
      const response = await knowledgeBaseApi.getArticles({
        category: selectedCategory?.slug,
        locale,
      });
      return response.data?.data || response.data || { articles: [] };
    },
    enabled: !!selectedCategory,
  });

  // Search articles
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ["kb-search", searchQuery, locale],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const response = await knowledgeBaseApi.searchArticles(
        searchQuery,
        locale,
      );
      return response.data?.data || response.data || [];
    },
    enabled: searchQuery.length >= 2,
  });

  // Fetch featured articles
  const { data: featuredArticles } = useQuery({
    queryKey: ["kb-featured", locale],
    queryFn: async () => {
      const response = await knowledgeBaseApi.getFeaturedArticles(locale);
      return response.data?.data || response.data || [];
    },
  });

  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const articles = Array.isArray(articlesData?.articles)
    ? articlesData.articles
    : [];
  const featured = Array.isArray(featuredArticles) ? featuredArticles : [];

  // Handle feedback
  const handleFeedback = async (articleId, helpful) => {
    if (feedbackGiven[articleId]) return;
    try {
      await knowledgeBaseApi.submitFeedback(articleId, helpful);
      setFeedbackGiven((prev) => ({ ...prev, [articleId]: true }));
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  // Filter categories based on search (client-side for quick filtering)
  const displayCategories =
    searchQuery.length >= 2
      ? categories.filter(
          (cat) =>
            cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            cat.description?.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : categories;

  // Articles to display
  const displayArticles =
    searchQuery.length >= 2 ? searchResults || [] : articles;

  // Article detail view
  if (selectedArticle) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedArticle(null)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight truncate">
              {selectedArticle.title}
            </h1>
            {selectedArticle.excerpt && (
              <p className="text-muted-foreground truncate">
                {selectedArticle.excerpt}
              </p>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              {selectedArticle.category_name && (
                <Badge variant="secondary">
                  {selectedArticle.category_name}
                </Badge>
              )}
              {selectedArticle.is_featured && (
                <Badge variant="outline" className="gap-1">
                  <Star className="w-3 h-3" />
                  Featured
                </Badge>
              )}
            </div>
            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground">
              <ReactMarkdown>{selectedArticle.content}</ReactMarkdown>
            </div>

            {/* Feedback */}
            <div className="mt-8 pt-6 border-t">
              <p className="text-sm text-muted-foreground mb-3">
                Was this article helpful?
              </p>
              {feedbackGiven[selectedArticle.id] ? (
                <p className="text-sm text-muted-foreground">
                  Thanks for your feedback!
                </p>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFeedback(selectedArticle.id, true)}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Yes
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFeedback(selectedArticle.id, false)}
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    No
                  </Button>
                </div>
              )}
            </div>

            {/* Related articles */}
            {selectedArticle.related_articles?.length > 0 && (
              <div className="mt-8 pt-6 border-t">
                <h3 className="font-medium mb-3">Related Articles</h3>
                <div className="space-y-2">
                  {selectedArticle.related_articles.map((related) => (
                    <button
                      key={related.id}
                      onClick={() => {
                        // Fetch full article
                        knowledgeBaseApi
                          .getArticle(related.slug, locale)
                          .then((res) => {
                            const data = res.data?.data || res.data;
                            setSelectedArticle(data);
                          });
                      }}
                      className="block w-full text-left p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <p className="font-medium">{related.title}</p>
                      {related.excerpt && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                          {related.excerpt}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Category detail view
  if (selectedCategory) {
    const Icon = iconMap[selectedCategory.icon] || BookOpen;
    const colors = getColorClasses(selectedCategory.color);

    const renderArticles = () =>
      displayArticles.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No articles in this category yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayArticles.map((article) => (
            <button
              key={article.id}
              onClick={() => {
                // Fetch full article with content
                knowledgeBaseApi
                  .getArticle(article.slug, locale)
                  .then((res) => {
                    const data = res.data?.data || res.data;
                    setSelectedArticle(data);
                  });
              }}
              className="block w-full text-left p-4 rounded-lg border hover:bg-muted/50 hover:border-primary/50 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium group-hover:text-primary transition-colors">
                      {article.title}
                    </p>
                    {article.is_featured && (
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    )}
                  </div>
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {article.excerpt}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </button>
          ))}
        </div>
      );

    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedCategory(null)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div
            className={cn(
              "flex items-center justify-center w-12 h-12 rounded-lg shrink-0",
              colors.bg,
            )}
          >
            <Icon className={cn("h-6 w-6", colors.text)} />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight truncate">
              {selectedCategory.name}
            </h1>
            <p className="text-muted-foreground truncate">
              {selectedCategory.description}
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            {articlesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              renderArticles()
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderSearchResults = () =>
    searchResults?.length === 0 ? (
      <Card>
        <CardContent className="py-12 text-center">
          <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-medium mb-2">
            {t("common.noResultsFound", "No results found")}
          </h3>
          <p className="text-sm text-muted-foreground">
            Try different keywords or browse the topics below
          </p>
        </CardContent>
      </Card>
    ) : (
      <div className="space-y-2">
        {searchResults?.map((article) => (
          <Card
            key={article.id}
            className="hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => {
              knowledgeBaseApi.getArticle(article.slug, locale).then((res) => {
                const data = res.data?.data || res.data;
                setSelectedArticle(data);
              });
            }}
          >
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{article.title}</p>
                  {article.category_name && (
                    <p className="text-sm text-muted-foreground">
                      in {article.category_name}
                    </p>
                  )}
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                      {article.excerpt}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );

  const renderCategories = () =>
    categories.length === 0 ? (
      <Card>
        <CardContent className="py-12 text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-medium mb-2">No categories yet</h3>
          <p className="text-sm text-muted-foreground">
            Check back later for help articles
          </p>
        </CardContent>
      </Card>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayCategories.map((category) => {
          const Icon = iconMap[category.icon] || BookOpen;
          const colors = getColorClasses(category.color);

          return (
            <Card
              key={category.id}
              className="hover:border-primary/50 transition-colors cursor-pointer group"
              onClick={() => setSelectedCategory(category)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div
                    className={cn(
                      "flex items-center justify-center w-10 h-10 rounded-lg",
                      colors.bg,
                    )}
                  >
                    <Icon className={cn("h-5 w-5", colors.text)} />
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <CardTitle className="text-base mt-3">
                  {category.name}
                </CardTitle>
                <CardDescription>{category.description}</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground">
                  {category.article_count || 0}{" "}
                  {(category.article_count || 0) === 1 ? "article" : "articles"}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );

  // Main view - categories grid
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-heading tracking-tight">
              {t("knowledgeBase.title", "Knowledge Base")}
            </h1>
            <p className="text-muted-foreground">
              {t(
                "knowledgeBase.subtitle",
                "Find answers and learn how to use eScribe",
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder={t(
                "knowledgeBase.searchPlaceholder",
                "Search articles...",
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base"
            />
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchQuery.length >= 2 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("knowledgeBase.searchResults", "Search Results")}
          </h2>

          {searchLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : (
            renderSearchResults()
          )}
        </div>
      )}

      {/* Featured Articles */}
      {!searchQuery && featured.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Featured Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((article) => (
              <Card
                key={article.id}
                className="hover:border-primary/50 transition-colors cursor-pointer group"
                onClick={() => {
                  knowledgeBaseApi
                    .getArticle(article.slug, locale)
                    .then((res) => {
                      const data = res.data?.data || res.data;
                      setSelectedArticle(data);
                    });
                }}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium group-hover:text-primary transition-colors">
                        {article.title}
                      </p>
                      {article.category_name && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {article.category_name}
                        </p>
                      )}
                      {article.excerpt && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {article.excerpt}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Categories Grid */}
      {!searchQuery && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("knowledgeBase.browseTopics", "Browse by Topic")}
          </h2>

          {categoriesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-40 w-full" />
              ))}
            </div>
          ) : (
            renderCategories()
          )}
        </div>
      )}

      {/* Help Footer */}
      {!searchQuery && (
        <Card className="bg-muted/50">
          <CardContent className="py-8">
            <div className="text-center max-w-xl mx-auto">
              <HelpCircle className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">
                {t("knowledgeBase.needMoreHelp", "Need more help?")}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t(
                  "knowledgeBase.contactDescription",
                  "Can't find what you're looking for? Our support team is here to help.",
                )}
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" asChild>
                  <a href="/api-docs.html" target="_blank">
                    <FileText className="h-4 w-4 mr-2" />
                    API Docs
                  </a>
                </Button>
                <Button asChild>
                  <a href="mailto:support@escribe.io">
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Support
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KnowledgeBase;
